import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for trapping focus within a container (modal, dialog, etc.)
 * This is important for accessibility - keyboard users should not be able
 * to tab outside of an open modal.
 *
 * @param {boolean} isActive - Whether the focus trap is active
 * @param {Object} options - Configuration options
 * @param {boolean} options.returnFocusOnDeactivate - Return focus to previously focused element when deactivated
 * @param {boolean} options.initialFocus - Selector for element to focus initially, or false to not focus
 * @returns {Object} - ref to attach to the container element
 */
function useFocusTrap(isActive, options = {}) {
  const {
    returnFocusOnDeactivate = true,
    initialFocus = true,
  } = options;

  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];

    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const elements = Array.from(
      containerRef.current.querySelectorAll(focusableSelectors)
    );

    // Filter out elements that are not visible
    return elements.filter(el => {
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  }, []);

  // Handle keydown for tab trapping
  const handleKeyDown = useCallback((event) => {
    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift + Tab: if on first element, go to last
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    // Tab: if on last element, go to first
    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
      return;
    }
  }, [getFocusableElements]);

  // Handle Escape key to close (optional - parent component should handle this)
  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape') {
      // Let the event bubble up to be handled by the modal component
      return;
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    // Store the currently focused element
    previousActiveElement.current = document.activeElement;

    // Add event listeners
    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      container.addEventListener('keydown', handleEscapeKey);
    }

    // Set initial focus
    if (initialFocus) {
      // Small delay to ensure modal is rendered
      const timeoutId = setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          // Try to focus the close button first, or the first focusable element
          const closeButton = containerRef.current?.querySelector('.modal-close, [data-close]');
          if (closeButton) {
            closeButton.focus();
          } else {
            focusableElements[0].focus();
          }
        } else if (containerRef.current) {
          // If no focusable elements, focus the container itself
          containerRef.current.setAttribute('tabindex', '-1');
          containerRef.current.focus();
        }
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        if (container) {
          container.removeEventListener('keydown', handleKeyDown);
          container.removeEventListener('keydown', handleEscapeKey);
        }
      };
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
        container.removeEventListener('keydown', handleEscapeKey);
      }
    };
  }, [isActive, initialFocus, handleKeyDown, handleEscapeKey, getFocusableElements]);

  // Return focus when deactivated
  useEffect(() => {
    if (!isActive && returnFocusOnDeactivate && previousActiveElement.current) {
      // Small delay to ensure the modal is fully closed
      const timeoutId = setTimeout(() => {
        if (previousActiveElement.current && typeof previousActiveElement.current.focus === 'function') {
          previousActiveElement.current.focus();
        }
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [isActive, returnFocusOnDeactivate]);

  return containerRef;
}

export default useFocusTrap;
